<template>
  <div class="detail">
    <!-- Выделенный на распределении диапазон -->
    <template v-if="!sel && brushSel">
      <div class="crumbs">
        <span>Обзор</span><span>→</span>
        <span class="cur">Выделение · {{ blocks[brushSel.bi] }}</span>
      </div>
      <div class="dhead">
        <span class="big">{{ brushWords.length }}</span>
        <span class="delta">
          слов в диапазоне {{ fmt(brushSel.lo) }} — {{ fmt(brushSel.hi) }}
          · среднее {{ fmt(brushAvg) }} · медиана блока {{ fmt(medians[brushSel.bi] || 0) }}
        </span>
      </div>
      <table class="dtable" v-if="brushWords.length">
        <thead>
          <tr><th style="width:45%">слово</th><th>значение</th><th>к медиане</th></tr>
        </thead>
        <tbody>
          <tr v-for="item in brushWords" :key="item.key" class="dc-phrase"
              title="открыть ячейку слова на этом блоке"
              @click="$emit('select-cell', item.key, brushSel.bi)">
            <td>{{ item.word }}</td>
            <td>{{ fmt(item.v) }}</td>
            <td :class="{ pos: item.v > (medians[brushSel.bi] || 0) }">
              {{ gap(item.v, medians[brushSel.bi]) }}
            </td>
          </tr>
        </tbody>
      </table>
      <div v-else class="dempty">В диапазон не попало ни одного слова.</div>
    </template>

    <!-- Пусто -->
    <template v-else-if="!sel">
      <div class="crumbs"><span class="cur">Обзор</span></div>
      <div class="dempty">
        Клик по слову — его профиль по блокам.<br>
        Клик по ячейке — utm-фразы этого слова на этом блоке.
      </div>
    </template>

    <!-- Сравнение двух слов -->
    <template v-else-if="cmp && cmpWord">
      <div class="crumbs"><span class="cur">Сравнение</span></div>
      <div class="dsub">итог по последнему блоку</div>
      <div class="dhead">
        <span>
          <span class="big" :style="{ color: 'var(--accent)' }">{{ fmt(lastOf(word)) }}</span>
          <span class="cmp-name" :style="{ color: 'var(--accent)' }">{{ word.label }}</span>
        </span>
        <span>
          <span class="big" :style="{ color: 'var(--cmp)' }">{{ fmt(lastOf(cmpWord)) }}</span>
          <span class="cmp-name" :style="{ color: 'var(--cmp)' }">{{ cmpWord.label }}</span>
        </span>
        <span class="delta">разница {{ gap(lastOf(word), lastOf(cmpWord)) }}</span>
      </div>
      <svg :viewBox="'0 0 ' + W + ' ' + H" role="img" aria-label="Сравнение двух слов">
        <line v-for="g in [0, 0.5, 1]" :key="g"
              :x1="PL" :y1="y(g)" :x2="W - PR" :y2="y(g)" stroke="var(--grid)" stroke-width="1"/>
        <polyline :points="points(cmpWord)" fill="none" stroke="var(--cmp)" stroke-width="2"
                  stroke-linejoin="round" stroke-linecap="round"/>
        <polyline :points="points(word)" fill="none" stroke="var(--accent)" stroke-width="2"
                  stroke-linejoin="round" stroke-linecap="round"/>
      </svg>
    </template>

    <!-- Профиль слова -->
    <template v-else-if="sel.bi === undefined">
      <div class="crumbs">
        <button type="button" class="lnk" @click="$emit('clear')">Обзор</button>
        <span>→</span><span class="cur">{{ word.label }}</span>
      </div>
      <div class="dhead">
        <span class="big">{{ fmt(lastOf(word)) }}</span>
        <span class="delta">итог по «{{ blocks[blocks.length - 1] }}»</span>
      </div>
      <svg :viewBox="'0 0 ' + W + ' ' + H" role="img" :aria-label="'Траектория ' + word.label">
        <line v-for="g in [0, 0.5, 1]" :key="g"
              :x1="PL" :y1="y(g)" :x2="W - PR" :y2="y(g)" stroke="var(--grid)" stroke-width="1"/>
        <polygon v-if="iqr" :points="iqrBand" fill="var(--grid)" opacity=".55"/>
        <polyline :points="points(word)" fill="none" stroke="var(--accent)" stroke-width="2"
                  stroke-linejoin="round" stroke-linecap="round"/>
        <circle v-for="(c, bi) in word.cells" :key="bi"
                :cx="x(bi)" :cy="y(pick(c))" r="3" fill="var(--accent)"
                stroke="var(--surface-1)" stroke-width="2"/>
      </svg>
      <div class="dstats">
        <div class="dstat"><div class="dl">{{ word.seg ? 'сессий в сегменте' : 'сессий за период' }}</div><div class="dv num">{{ word.ses }}</div></div>
        <div class="dstat"><div class="dl">отток к итогу</div><div class="dv num">{{ raw(word.cells[blocks.length - 1]) }}</div></div>
        <div class="dstat"><div class="dl">итог</div><div class="dv num">{{ fmt(lastOf(word)) }}</div></div>
        <div class="dstat"><div class="dl">блоков</div><div class="dv num">{{ blocks.length }}</div></div>
      </div>
      <div class="dsub" style="margin-top:12px">Клик по ячейке столбца — utm-фразы этого блока.</div>

      <!-- Конверсии по фразам со словом -->
      <div class="dc-conv">
        <div class="dc-conv-head">
          {{ word.seg ? 'Конверсии по фразам сегмента' : 'Конверсии по фразам со словом' }}
          <b class="num">{{ wordConvTotal }}</b>
        </div>
        <table class="dtable" v-if="convPhrases.length">
          <thead>
            <tr><th style="width:60%">utm_term</th><th>формы</th><th>микро</th><th>всего</th></tr>
          </thead>
          <tbody>
            <template v-for="item in convPhrases" :key="item.phrase">
              <tr class="dc-phrase" @click="togglePhrase(item.phrase)">
                <td>{{ expanded[item.phrase] ? '▾' : '▸' }} {{ item.phrase }}</td>
                <td>{{ item.form.length }}</td>
                <td>{{ item.micro.length }}</td>
                <td class="pos">{{ item.form.length + item.micro.length }}</td>
              </tr>
              <tr v-if="expanded[item.phrase]">
                <td colspan="4" class="dc-conv-detail">
                  <div class="dc-cols">
                    <div>
                      <div class="dc-col-head">Конверсии с форм <b class="num">{{ item.form.length }}</b></div>
                      <div v-if="!item.form.length" class="dc-none">нет</div>
                      <div v-for="(e, i) in item.form" :key="'f' + i" class="dc-when num">{{ when(e.t) }}</div>
                    </div>
                    <div>
                      <div class="dc-col-head">Микроконверсии <b class="num">{{ item.micro.length }}</b></div>
                      <div v-if="!item.micro.length" class="dc-none">нет</div>
                      <div v-for="(e, i) in item.micro" :key="'m' + i" class="dc-when num">
                        {{ when(e.t) }} <span class="dc-type">{{ e.type }}</span>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <div v-else class="dempty">Конверсий по фразам {{ word.seg ? 'сегмента' : 'с этим словом' }} за период нет.</div>
        <div v-if="convPhrasesEmpty" class="dsub">
          Ещё {{ convPhrasesEmpty }} {{ plural(convPhrasesEmpty, ['фраза', 'фразы', 'фраз']) }} {{ word.seg ? 'сегмента' : 'с этим словом' }} — без конверсий.
        </div>
      </div>
    </template>

    <!-- Drill-down по ячейке -->
    <template v-else>
      <div class="crumbs">
        <button type="button" class="lnk" @click="$emit('clear')">Обзор</button>
        <span>→</span>
        <button type="button" class="lnk" @click="$emit('select-word', sel.key)">{{ word.label }}</button>
        <span>→</span><span class="cur">{{ blocks[sel.bi] }}</span>
      </div>
      <div class="dhead">
        <span class="big">{{ fmt(pick(word.cells[sel.bi])) }}</span>
        <span class="delta">{{ raw(word.cells[sel.bi]) }} {{ plural(raw(word.cells[sel.bi]), ['сессия', 'сессии', 'сессий']) }} · {{ gap(pick(word.cells[sel.bi]), median) }} к медиане блока</span>
      </div>
      <div class="dsub">
        Фразы utm_term{{ word.seg ? ' сегмента' : ', содержащие это слово,' }} в том же порядке, что и в разрезе по utm_term.
        Порог «мин. сессий со словом» здесь не применяется.
      </div>

      <table class="dtable" v-if="phrases.length">
        <thead>
          <tr><th style="width:45%">utm_term</th><th>Метрики</th></tr>
        </thead>
        <tbody>
          <template v-for="item in phrases" :key="item.phrase">
            <tr class="dc-phrase" @click="togglePhrase(item.phrase)">
              <td>{{ expanded[item.phrase] ? '▾' : '▸' }} {{ item.phrase }}</td>
              <td>{{ formatMetrics(item.metrics) }}</td>
            </tr>
            <tr v-if="expanded[item.phrase]">
              <td colspan="2" class="dc-rules">
                <BalansirRules :phrase="item.phrase"/>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <div v-else class="dempty">Фраз {{ word.seg ? 'сегмента' : 'с этим словом' }} на этом блоке не найдено.</div>
    </template>
  </div>
</template>

<script>
import { formatMetrics, splitPhraseToWords } from '@/utils/wordStat';
import { plural } from '@/utils/segments';
import BalansirRules from '@/components/BalansirRules.vue';

export default {
  name: 'DrillCard',
  components: { BalansirRules },
  props: {
    blocks: { type: Array, required: true },
    // Все столбцы К4 — слова и сегменты. У слов есть ещё wi: по нему приходит
    // выделение с распределения (WordStrips живёт на числовых индексах).
    columns: { type: Array, required: true },
    raw: { type: Function, required: true },
    pick: { type: Function, required: true },
    sel: { type: Object, default: null },          // null | {key} | {key, bi}
    cmp: { type: Boolean, default: false },
    cmpKey: { type: String, default: null },
    iqr: { type: Boolean, default: false },
    quarts: { type: Array, default: () => [] },
    medians: { type: Array, default: () => [] },
    // Сырой sortedObjects: нужен, чтобы вытащить фразы блока без пересборки.
    sortedObjects: { type: Object, default: null },
    // Конверсии в разрезе utm_term: { фраза: [{t, form, type}] }.
    convByPhrase: { type: Object, default: null },
    brushSel: { type: Object, default: null },
  },
  emits: ['clear', 'select-word', 'select-cell'],
  data() {
    return {
      W: 460, H: 150, PL: 10, PR: 34, PT: 12, PB: 22,
      expanded: {},
    };
  },
  computed: {
    byKey() {
      const m = new Map();
      for (const c of this.columns) m.set(c.key, c);
      return m;
    },
    word() {
      return this.sel ? this.byKey.get(this.sel.key) || null : null;
    },
    cmpWord() {
      return this.cmpKey === null ? null : this.byKey.get(this.cmpKey) || null;
    },
    // Фразы столбца: у слова — все фразы с ним, у сегмента — его состав.
    phraseSet() {
      return new Set(this.word ? this.word.phrases : []);
    },
    median() {
      return this.sel && this.sel.bi !== undefined ? (this.medians[this.sel.bi] || 0) : 0;
    },
    phrases() {
      if (!this.sel || this.sel.bi === undefined || !this.sortedObjects) return [];
      const block = this.blocks[this.sel.bi];
      const utm = (this.sortedObjects[block] && this.sortedObjects[block].utm) || {};
      // Порядок фраз не трогаем: бэкенд уже отдал utm отсортированным по ch
      // убыванию, и он должен совпадать с колонкой «Разрез по utm_term».
      return Object.keys(utm)
        .filter(phrase => this.phraseSet.has(phrase))
        .map(phrase => ({ phrase: phrase, metrics: utm[phrase] }));
    },
    // Фразы со словом, у которых есть конверсии, — от большего к меньшему.
    // Фразы без конверсий в таблицу не идут: на широком срезе их сотни,
    // и они утопили бы то, ради чего таблица нужна. Их число — строкой ниже.
    convPhrases() {
      if (!this.word || !this.convByPhrase) return [];
      const out = [];
      for (const phrase in this.convByPhrase) {
        if (!Object.prototype.hasOwnProperty.call(this.convByPhrase, phrase)) continue;
        if (!this.convMatch(phrase)) continue;
        const events = this.convByPhrase[phrase] || [];
        if (!events.length) continue;
        out.push({
          phrase: phrase,
          form: events.filter(e => e.form),
          micro: events.filter(e => !e.form),
        });
      }
      out.sort((a, b) => (b.form.length + b.micro.length) - (a.form.length + a.micro.length)
        || a.phrase.localeCompare(b.phrase, 'ru'));
      return out;
    },
    // Сколько фраз столбца осталось за таблицей. Считаем по срезу матрицы,
    // а не по ответу конверсий: там фразы без конверсий вообще не приходят.
    convPhrasesEmpty() {
      if (!this.word) return 0;
      return Math.max(0, this.word.phrases.length - this.convPhrases.length);
    },
    wordConvTotal() {
      return this.convPhrases.reduce((sum, i) => sum + i.form.length + i.micro.length, 0);
    },
    brushWords() {
      if (!this.brushSel) return [];
      const bi = this.brushSel.bi;
      const byWi = new Map();
      for (const c of this.columns) if (!c.seg) byWi.set(c.wi, c);
      return this.brushSel.wis
        .map(wi => byWi.get(wi))
        .filter(Boolean)
        .map(w => ({ key: w.key, word: w.label, v: this.pick(w.cells[bi]) }))
        .sort((a, b) => b.v - a.v);
    },
    brushAvg() {
      if (!this.brushWords.length) return 0;
      return this.brushWords.reduce((sum, i) => sum + i.v, 0) / this.brushWords.length;
    },
    iqrBand() {
      if (!this.quarts.length) return '';
      const top = this.quarts.map((q, bi) => this.x(bi) + ',' + this.y(q[1])).join(' ');
      const bottom = this.quarts.slice().reverse()
        .map((q, i) => this.x(this.quarts.length - 1 - i) + ',' + this.y(q[0])).join(' ');
      return top + ' ' + bottom;
    },
  },
  watch: {
    sel() {
      this.expanded = {};
    },
  },
  methods: {
    formatMetrics: formatMetrics,
    fmt(value) {
      return Math.round((Number(value) || 0) * 100) + ' %';
    },
    // Разница из уже округлённых процентов: иначе рядом с «80 %» и «80 %» стояло бы «-1».
    gap(a, b) {
      const v = Math.round((Number(a) || 0) * 100) - Math.round((Number(b) || 0) * 100);
      return (v > 0 ? '+' : '') + v;
    },
    plural: plural,
    lastOf(w) {
      return this.pick(w.cells[w.cells.length - 1]);
    },
    x(bi) {
      const n = Math.max(1, this.blocks.length - 1);
      return this.PL + bi * (this.W - this.PL - this.PR) / n;
    },
    y(share) {
      const v = Math.min(1, Math.max(0, Number(share) || 0));
      return this.PT + (1 - v) * (this.H - this.PT - this.PB);
    },
    points(w) {
      return w.cells.map((c, bi) => this.x(bi) + ',' + this.y(this.pick(c))).join(' ');
    },
    // Слово — той же токенизацией, что conversionsByWord для числа «c» в шапке: иначе
    // итог карточки расходился бы с шапкой на фразах вне среза матрицы. Сегмент — по
    // своему составу, точным совпадением фразы.
    convMatch(phrase) {
      if (this.word.seg) return this.phraseSet.has(phrase);
      return splitPhraseToWords(phrase).includes(this.word.label);
    },
    togglePhrase(phrase) {
      this.expanded[phrase] = !this.expanded[phrase];
    },
    // Время конверсии приходит ISO-строкой в UTC; показываем в часовом поясе
    // браузера — с ним же сверяется пользователь, глядя в свою CRM.
    when(iso) {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return String(iso);
      const pad = n => String(n).padStart(2, '0');
      return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear()
        + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
    },
  },
};
</script>

<style scoped>
.cmp-name { font-size: 12.5px; font-weight: 650; margin-left: 6px; }
.dc-phrase { cursor: pointer; }
.dc-phrase:hover td { color: var(--text-primary); }
.dc-rules { background: var(--page); }

.dc-conv { margin-top: 18px; border-top: 1px solid var(--grid); padding-top: 14px; }
.dc-conv-head {
  font-size: 13px; color: var(--text-secondary);
  display: flex; align-items: baseline; gap: 8px;
}
.dc-conv-head b { color: var(--text-primary); font-size: 16px; font-weight: 700; }
.dc-conv-detail { background: var(--page); }
/* Две колонки, но на узком экране — одна под другой: список дат в 150 px
   переносится по символам и читается хуже, чем в столбик. */
.dc-cols { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 6px 18px; }
.dc-col-head {
  font-size: 11.5px; color: var(--text-muted); margin-bottom: 4px;
  display: flex; align-items: baseline; gap: 6px;
}
.dc-col-head b { color: var(--text-secondary); font-weight: 650; }
.dc-when { font-size: 12px; color: var(--text-secondary); text-align: left; }
.dc-type { color: var(--text-muted); font-size: 11px; }
.dc-none { font-size: 12px; color: var(--text-muted); }
</style>
